import React, { Suspense, lazy, useEffect } from 'react'
import {
  AnonSessionRoute,
  AuthRoute,
  NotFound,
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
} from '@fs/zion-router'
import { targetEnv } from '@fs/zion-config'
import * as Sentry from '@sentry/react'
import ErrorBoundary from '@fs/zion-error-boundary'
import { RecordAuthRoute } from '@fs/zion-gedcomx'
import { LoadingSkeleton as RecordLoadingSkeleton } from '@fs/zion-record-details'
import Cookies from 'js-cookie'
import { useContainerWidth } from '@fs/zion-ui'
import { useUser } from '@fs/zion-user'
import useMeasure from 'react-use-measure'
import { useFeatureFlag } from '@fs/zion-flags'
import UnknownError from './components/error-components/UnknownError'
import NavigationTabs from './components/hr/NavigationTabs'
import lightningjs from './lightningjs'
import CenteredSpinner from './components/shared/CenteredSpinner'
import ImageAuthRoute from './components/shared/auth-routes/ImageAuthRoute'
import AffiliateAccessRoute from './components/shared/auth-routes/AffiliateAccessRoute'
import useFilmViewerRedirects from './components/film-viewer/useFilmViewerRedirects'
import useCatalogRedirects from './components/catalog/useCatalogRedirects'
import useSearchQueryRedirect from './components/shared/useSearchQueryRedirect'
import componentLoader from './componentLoader'
import useCmsCollectionGating from './components/shared/useCmsCollectionGating'

const Suspend = ({ children }) => <Suspense fallback={<CenteredSpinner />}>{children}</Suspense>

// Dynamically load components to reduce bundle size
// https://reactjs.org/docs/react-api.html#reactlazy
const CatalogDetails = lazy(() =>
  componentLoader(() => import('./components/catalog/catalog-details/CatalogDetailsPage'))
)
const OldCatalogDetails = lazy(() =>
  componentLoader(() => import('./components/catalog/old-catalog-details/CatalogDetailsPage'))
)
const JiapuPage = lazy(() => componentLoader(() => import('./components/jiapu-collection/JiapuPage')))
const CatalogResultsPage = lazy(() => componentLoader(() => import('./components/catalog/CatalogResultsPage')))
const CatalogSearchPage = lazy(() => componentLoader(() => import('./components/catalog/CatalogSearchPage')))
const CatalogPrintListPage = lazy(() => componentLoader(() => import('./components/catalog/CatalogPrintListPage')))
const CatalogFilmRedirectPage = lazy(() =>
  componentLoader(() => import('./components/catalog/CatalogFilmRedirectPage'))
)
const JiapuAttach = lazy(() => componentLoader(() => import('./components/prototypes/jiapu-attach/JiapuAttach')))

const CmsCollectionDetails = lazy(() =>
  componentLoader(() => import('./components/cms-collections/CmsCollectionDetailsPage'))
)
const CollectionListPage = lazy(() => componentLoader(() => import('./components/hr/CollectionListPage')))
const FilmDebugPage = lazy(() => componentLoader(() => import('./components/prototypes/film-debug/FilmDebugPage')))
const PdfTestPage = lazy(() => componentLoader(() => import('./components/prototypes/pdf-test/PdfTestPage')))
const GenCollectionDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenCollectionDetailsPage')))
const GenSearchPage = lazy(() => componentLoader(() => import('./components/gen/GenSearchPage')))
const GenResultsPage = lazy(() => componentLoader(() => import('./components/gen/GenResultsPage')))
const GenDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenDetailsPage')))
const GenSubmissionsPage = lazy(() => componentLoader(() => import('./components/gen/GenSubmissionsPage')))
const GenSubmissionDetailsPage = lazy(() => componentLoader(() => import('./components/gen/GenSubmissionDetailsPage')))
const HRComparePage = lazy(() => componentLoader(() => import('./components/prototypes/hr/HRComparePage')))
const LocationListPage = lazy(() => componentLoader(() => import('./components/location/LocationListPage')))
const LocationPage = lazy(() => componentLoader(() => import('./components/location/LocationPage')))
const RecordDebugPage = lazy(() =>
  componentLoader(() => import('./components/prototypes/record-debug/RecordDebugPage'))
)
const RecordDetailsPage = lazy(() => componentLoader(() => import('./components/hr/RecordDetailsPage')))
const RecordRedirectToPersona = lazy(() => componentLoader(() => import('./components/hr/RecordRedirectToPersona')))
const SearchHomePage = lazy(() => componentLoader(() => import('./components/hr/SearchHomePage')))
const SearchResultsPage = lazy(() => componentLoader(() => import('./components/hr/SearchResultsPage')))
const SearchResultsLoadingSkeleton = lazy(() =>
  componentLoader(() => import('@fs/zion-record-search').then((module) => ({ default: module.LoadingSkeletons })))
)
const SourceLinkerPage = lazy(() => componentLoader(() => import('./components/sourcelinker/SourceLinkerPage')))

const TreeSearchPage = lazy(() => componentLoader(() => import('./components/tree/TreeSearchPage')))
const TreeResultsPage = lazy(() => componentLoader(() => import('./components/tree/TreeResultsPage')))
const TreeCollectionDetailsPage = lazy(() =>
  componentLoader(() => import('./components/tree/TreeCollectionDetailsPage'))
)
const TreeBrowsePage = lazy(() => componentLoader(() => import('./components/tree/TreeBrowsePage')))
const TreeToTreeLinkerPage = lazy(() =>
  componentLoader(() => import('./components/sourcelinker/tree-to-tree/TreeToTreePage'))
)

const FilmViewerPage = lazy(() => componentLoader(() => import('./components/film-viewer/FilmViewerPage')))

const FullTextSearchPage = lazy(() => componentLoader(() => import('./components/full-text-search/FullTextSearchPage')))
const FullTextResultsPage = lazy(() =>
  componentLoader(() => import('./components/full-text-search/FullTextResultsPage'))
)
const FullTextCollectionDetailsPage = lazy(() =>
  componentLoader(() => import('./components/full-text-search/FullTextCollectionDetailsPage'))
)
const FullTextCollectionsListPage = lazy(() =>
  componentLoader(() => import('./components/full-text-search/FullTextCollectionsListPage'))
)
const MormonGenealogicalResearchPage = lazy(() =>
  componentLoader(() => import('./components/hr/mormon-genealogy/MormonGenealogicalResearchPage'))
)

/**
 * ErrorBoundary's errorHandler for uncaught render errors.
 *
 * Only sets scope data — it deliberately does not capture. ErrorBoundary's own componentDidCatch
 * captures this same error immediately after calling errorHandler, and reads the current scope
 * when it does, so capturing here would send a second, duplicate event.
 *
 * @param {Error} err the render error (captured by ErrorBoundary, not here)
 * @param {string} componentStack the component stack, passed by ErrorBoundary as a string
 */
export function handleRenderError(err, componentStack) {
  // Sentry v8+ routes the top-level setTag/setContext to the isolation scope, so that is the
  // scope to read the previous values off of and restore them on. Event assembly merges the
  // isolation scope into whatever scope ErrorBoundary captures from, so these still land.
  const scope = Sentry.getIsolationScope()
  const { tags: previousTags, contexts: previousContexts } = scope.getScopeData()
  const previousCodeId = previousTags.codeId
  const previousExtraInfo = previousContexts.extraInfo

  Sentry.setTag('codeId', 1001)
  Sentry.setContext('extraInfo', { codeId: 1001, componentStack })

  Promise.resolve().then(() => {
    // Passing undefined removes the tag — v10 dropped scope.removeTag().
    scope.setTag('codeId', previousCodeId)
    scope.setContext('extraInfo', previousExtraInfo ?? null)
  })
}

export function CmsTreeCollectionDetailsPage(props) {
  return <CmsCollectionDetails {...props} dataType="tree" />
}

// FSS-15216/FSS-15144: on = catalog details on the CatalogItem model (and, once the orchestration
// work lands, the proxy); off = the pre-model page on search backend, in old-catalog-details/.
// Exported so App.test.js can cover the swap without standing up the whole route tree.
export function FlaggedCatalogDetails(props) {
  const catalogRmsOrchestrationFlag = useFeatureFlag('search_catalogRmsOrchestration')
  return catalogRmsOrchestrationFlag?.isOn ? <CatalogDetails {...props} /> : <OldCatalogDetails {...props} />
}

const EMBEDDED_MOBILE_COOKIE_NAME = 'embeddedMobile'

function App({ setUseFatFooter }) {
  // Hide NavigationTabs if page is embedded in mobile app WebView
  const embeddedMobile = !!Cookies.get(EMBEDDED_MOBILE_COOKIE_NAME)
  const atWidth = useContainerWidth()
  const isMobile = atWidth({ default: true, xl: false })
  const hideSubNav = isMobile || embeddedMobile

  const { signedIn } = useUser()
  const { pathname, search } = useLocation()
  const homePage = '/search/'

  useEffect(() => {
    // eslint-disable-next-line @babel/no-unused-expressions
    window.lightningjs || lightningjs

    if (pathname.match(/\/location\/+\/+/i)) {
      window.usabilla_live = window.lightningjs.require('usabilla_live', '//w.usabilla.com/08c3d906fb80.js')
      window.usabilla_live('virtualPageView')
    }

    if (pathname === homePage && !signedIn) {
      setUseFatFooter(true)
    } else {
      setUseFatFooter(false)
    }
  }, [homePage, pathname, setUseFatFooter, signedIn])

  const query = useHistory()?.location?.search || ''

  const {
    filmViewerHighlightRedirectUrl,
    filmViewerRedirectArkUri,
    filmViewerWaypointRedirectUri,
    filmViewerWaypointArkRedirectUri,
    filmViewerBrokenWaypointLink,
    filmViewerBadOpenedWaypoints,
    filmViewerExtraneousWaypointContext,
    filmViewerRedirectOpenWaypointChooser,
  } = useFilmViewerRedirects()

  const { catalogShowRedirectUrl, catalogDeprecatedPathUrl } = useCatalogRedirects()
  const searchQueryRedirect = useSearchQueryRedirect()

  const [navRef, navBounds] = useMeasure()
  const subnavHeight = Math.ceil(navBounds?.height || 0)
  // FSS-14429 route-guard swaps: on = new guards (ImageAuthRoute, AffiliateAccessRoute, public
  // waypoint chooser), off = previous RecordAuthRoute behavior
  const routeAuthGuardsFlag = useFeatureFlag('search_routeAuthGuards')
  const { cmsCollectionsFlag } = useCmsCollectionGating()
  const signedOutResultsFlag = useFeatureFlag('search_signedOutResults')

  const redirectUrlForLangSlashArk = getRedirectUrlForLangSlashArk({ pathname, search })
  if (redirectUrlForLangSlashArk) {
    // NOTE: return <Redirect to={redirectUrlForLangSlashArk} /> does not work here because pathname does not include the language code at the beginning of the path
    window.history.replaceState({}, '', redirectUrlForLangSlashArk)
  }

  // "Switch" only switches on direct children, so routes and redirects have to be direct children.
  return (
    <div style={{ '--subnav-height': `${subnavHeight}px`, height: '100%' }}>
      <ErrorBoundary FallbackComponent={UnknownError} errorHandler={handleRenderError}>
        <Suspense fallback={<CenteredSpinner />}>
          {!hideSubNav && <NavigationTabs navRef={navRef} />}

          {
            <Switch>
              {/* Film Viewer Routes */}
              {/* redirect to waypoint chooser using uri hash/path (see getFilmViewerWaypointRedirectUri function) */}
              {filmViewerWaypointRedirectUri && <Redirect from="/search/image" to={filmViewerWaypointRedirectUri} />}
              {/* redirect to image using uri hash/path (see getFilmViewerWaypointArkRedirectUri function) */}
              {filmViewerWaypointArkRedirectUri && (
                <Redirect
                  from="/ark\:/61903/3\:(1|2)\::arkPath"
                  to={filmViewerWaypointArkRedirectUri.replace(/:/g, '\\:')}
                />
              )}
              {/* broken link that happened from a bug in old film viewer (see https://fhjira.churchofjesuschrist.org/browse/FSS-10252) */}
              {filmViewerBrokenWaypointLink && (
                <Redirect
                  from="/search/image/index"
                  to={{
                    pathname: '/search/image/index',
                  }}
                />
              )}
              {/* bad opened waypoints link that happened from a bug in old film viewer (see https://fhjira.churchofjesuschrist.org/browse/FSS-10225) */}
              {filmViewerBadOpenedWaypoints && (
                <Redirect from={pathname} to={{ pathname, search: filmViewerBadOpenedWaypoints?.search }} />
              )}
              {/* extraneous waypoint context param that happened from a bug in old film viewer where owc and wc were present */}
              {filmViewerExtraneousWaypointContext && (
                <Redirect
                  from={pathname}
                  to={{
                    pathname: filmViewerExtraneousWaypointContext?.pathname,
                    search: filmViewerExtraneousWaypointContext?.search,
                  }}
                />
              )}
              {/* redirect to give owc param full path (see getFilmViewerRedirectOpenWaypointChooser function) */}
              {filmViewerRedirectOpenWaypointChooser && (
                <Redirect
                  from={pathname}
                  to={{
                    pathname: filmViewerRedirectOpenWaypointChooser?.pathname,
                    search: filmViewerRedirectOpenWaypointChooser?.search,
                  }}
                />
              )}
              {/* 'waypoint-chooser' mode - waypoint viewer, /search/image/index */}
              {/* Public browse list: no login to browse; per-image permission is checked on drill-down. */}
              {routeAuthGuardsFlag.isOn ? (
                <AnonSessionRoute
                  path="/search/image/index"
                  component={FilmViewerPage}
                  LoadingComponent={CenteredSpinner}
                />
              ) : (
                <RecordAuthRoute
                  path="/search/image/index"
                  component={FilmViewerPage}
                  LoadingSkeleton={<CenteredSpinner />}
                />
              )}
              {/* 'dgs' mode - film viewer, /search/film/:dgsNum */}
              {routeAuthGuardsFlag.isOn ? (
                <AffiliateAccessRoute
                  path="/search/film/:dgsNum"
                  component={FilmViewerPage}
                  LoadingSkeleton={<CenteredSpinner />}
                />
              ) : (
                <RecordAuthRoute
                  path="/search/film/:dgsNum"
                  component={FilmViewerPage}
                  LoadingSkeleton={<CenteredSpinner />}
                />
              )}
              {/* 'ark' mode - ark viewer, /ark:/61903/:arkPath */}
              {routeAuthGuardsFlag.isOn && (
                <ImageAuthRoute
                  path="/ark\:/61903/3\:1\::arkPath"
                  component={FilmViewerPage}
                  LoadingSkeleton={<CenteredSpinner />}
                />
              )}
              {/* 3:2 film arks are film-level browse (same content as /search/film/:dgsNum), and DAS
                  has no per-image permission for them, so they use the same gate as DGS mode. */}
              {routeAuthGuardsFlag.isOn && (
                <AffiliateAccessRoute
                  path="/ark\:/61903/3\:2\::arkPath"
                  component={FilmViewerPage}
                  LoadingSkeleton={<CenteredSpinner />}
                />
              )}
              {!routeAuthGuardsFlag.isOn && (
                <RecordAuthRoute
                  path="/ark\:/61903/3\:(1|2)\::arkPath"
                  component={FilmViewerPage}
                  LoadingSkeleton={<CenteredSpinner />}
                />
              )}
              {/* redirect to an image with a `bounds` (highlights) query param */}
              {filmViewerHighlightRedirectUrl && (
                <Redirect
                  from="/search/image/highlight"
                  exact
                  to={`${filmViewerHighlightRedirectUrl.replace(/:/g, '\\:')}`}
                />
              )}
              {/* redirect to the `uri` param */}
              {filmViewerRedirectArkUri && (
                // the `replace` escapes `:` with `\:` so that React Router does not treat the ark as a match param
                <>
                  <Redirect from="/search/image/uri" exact to={filmViewerRedirectArkUri} />
                  <Redirect from="/search/image/viewer" exact to={filmViewerRedirectArkUri} />
                </>
              )}
              {/* empty landing - not a useful page - old film viewer was receiving traffic to this so we want it too */}
              <AnonSessionRoute
                path="/search/image/viewer"
                exact
                component={FilmViewerPage}
                LoadingSkeleton={<CenteredSpinner />}
              />
              {/* Full-Text Search Routes */}
              <AnonSessionRoute
                path="/search/full-text/collection/list"
                component={FullTextCollectionsListPage}
                requirePersonalData
              />
              {cmsCollectionsFlag?.isOn && (
                <AnonSessionRoute
                  path="/search/full-text/collection/:collectionId"
                  render={(props) => <CmsCollectionDetails {...props} dataType="full-text" />}
                  requirePersonalData
                />
              )}
              {!cmsCollectionsFlag?.isOn && (
                <AnonSessionRoute
                  path="/search/full-text/collection/:collectionId"
                  component={FullTextCollectionDetailsPage}
                  requirePersonalData
                />
              )}
              <AuthRoute path="/search/full-text/results" component={FullTextResultsPage} requirePersonalData />
              <AnonSessionRoute path="/search/full-text" component={FullTextSearchPage} requirePersonalData />
              {/* Simple Search retired (FSS-15093) - the standalone pages now live on Full-Text Search.
                  Redirects kept for bookmarks and for already-published links (e.g. older @fs/zion-record-details). */}
              <Redirect
                from="/search/simple-search/results"
                exact
                to={{ pathname: '/search/full-text/results', search: query }}
              />
              <Redirect from="/search/simple-search" exact to="/search/full-text" />
              {/* Redirect for localhost convenience only */}
              {targetEnv === 'local' && <Redirect from="/" exact to="/search/" />}
              {/* Search Home */}
              <AnonSessionRoute path="/search/" exact LoadingComponent={CenteredSpinner}>
                <Suspend>
                  <SearchHomePage />
                </Suspend>
              </AnonSessionRoute>
              {/* Record Details */}
              <RecordAuthRoute
                path="/ark\:/61903/1\:1\::arkPath"
                component={RecordDetailsPage}
                LoadingSkeleton={<RecordLoadingSkeleton />}
              />
              {/* Ark 1:2 paths are long-lived URLs for records, but full records don't have dedicated pages. */}
              {/* Instead, we redirect users to the first 1:1 persona in the record. */}
              {/* This route finds the first persona and does a redirect. */}
              {/* If the user doesn't have permission to see the record, we redirect to a login instead. */}
              <AnonSessionRoute path="/ark\:/61903/1\:2\::arkPath" component={RecordRedirectToPersona} />

              {/* These location redirects are required BEFORE the HR collections paths */}
              <Redirect from="/search/collection/location/:region" exact to={`/search/location/:region?${query}`} />
              <Redirect
                from="/search/collection/location/:region/:subRegion"
                exact
                to={`/search/location/:region/:subRegion?${query}`}
              />
              {/* IGI redirect: /igi is not a valid collection ID for search, redirect to collection 5 */}
              <Redirect from="/search/collection/igi" to="/search/collection/5" />
              {/* HR Collections */}
              <AnonSessionRoute path="/search/collection/list" component={CollectionListPage} />
              <AnonSessionRoute
                path="/1940census"
                render={(props) => <CmsCollectionDetails {...props} dataType="hr" />}
              />
              <AnonSessionRoute
                path="/search/collection/:collectionId"
                render={(props) => <CmsCollectionDetails {...props} dataType="hr" />}
              />
              {/* Locations */}
              <AnonSessionRoute path="/search/location/list" exact component={LocationListPage} />
              <AnonSessionRoute path="/search/location/:region" exact component={LocationPage} />
              <AnonSessionRoute path="/search/location/:region/:subRegion" exact component={LocationPage} />
              {/* HR Search Results */}
              {searchQueryRedirect && <Redirect exact from="/search/record/results" to={searchQueryRedirect} />}
              {signedOutResultsFlag.isOn ? (
                <AnonSessionRoute
                  path="/search/record/results"
                  component={SearchResultsPage}
                  LoadingSkeleton={<SearchResultsLoadingSkeleton />}
                />
              ) : (
                <RecordAuthRoute
                  path="/search/record/results"
                  component={SearchResultsPage}
                  LoadingSkeleton={<SearchResultsLoadingSkeleton />}
                />
              )}

              {/* Tree Search & Results */}
              {searchQueryRedirect && <Redirect exact from="/search/tree/results" to={searchQueryRedirect} />}
              <AuthRoute path="/search/tree/results" component={TreeResultsPage} />
              {/* Tree Browse List Page */}
              <AuthRoute path="/search/tree/list" component={TreeBrowsePage} />

              {/* Tree Linker */}

              <AuthRoute path="/search/tree/linker" component={TreeToTreeLinkerPage} />

              {/* /search/tree, /search/tree/id, and /search/tree/name */}
              <AnonSessionRoute path="/search/tree" exact component={TreeSearchPage} />

              <AnonSessionRoute path="/search/tree/:searchType" exact component={TreeSearchPage} />

              {/* /search/<collectionId>/<treeId>, /search/<collectionId>/<treeId>/id, and /search/tree/<collectionId>/<treeId>/name */}
              {cmsCollectionsFlag?.isOn && (
                <AuthRoute
                  path="/search/tree/:collectionId/:treeId/:searchType"
                  component={CmsTreeCollectionDetailsPage}
                />
              )}
              {cmsCollectionsFlag?.isOn && (
                <AuthRoute path="/search/tree/:collectionId/:treeId" exact component={CmsTreeCollectionDetailsPage} />
              )}
              {!cmsCollectionsFlag?.isOn && (
                <AuthRoute path="/search/tree/:collectionId/:treeId" exact component={TreeCollectionDetailsPage} />
              )}
              {!cmsCollectionsFlag?.isOn && (
                <AuthRoute
                  path="/search/tree/:collectionId/:treeId/:searchType"
                  component={TreeCollectionDetailsPage}
                />
              )}

              {/* Genealogies */}
              <AnonSessionRoute path="/search/genealogies" exact component={GenSearchPage} requirePersonalData />
              <AnonSessionRoute path="/search/genealogies/results" component={GenResultsPage} requirePersonalData />
              <AnonSessionRoute
                path="/search/genealogies/collection/:collectionId"
                component={GenCollectionDetailsPage}
                requirePersonalData
              />
              <AnonSessionRoute
                path="/search/genealogies/submissions"
                component={GenSubmissionsPage}
                requirePersonalData
              />
              <AnonSessionRoute
                path="/search/genealogies/submission/:collectionId/:submissionId"
                component={GenSubmissionDetailsPage}
                requirePersonalData
              />
              <AnonSessionRoute path="/search/cgt" exact component={GenSubmissionsPage} requirePersonalData />
              {/* Source Linker */}
              <AuthRoute path="/search/linker" component={SourceLinkerPage} />
              {/* Genealogies Details */}
              <AnonSessionRoute path="/ark\:/61903/:arkPath" component={GenDetailsPage} requirePersonalData />
              {/* Redirects for genealogies pages */}
              <Redirect exact from="/search/family-trees" to={`/search/genealogies${query}`} />
              <Redirect exact from="/search/family-trees/results" to={`/search/genealogies/results${query}`} />
              {/* Old routes for genealogies pages */}
              <Route path="/search/family-trees" exact component={GenSearchPage} requirePersonalData />
              <AnonSessionRoute path="/search/family-trees/results" component={GenResultsPage} requirePersonalData />
              {/* Catalog Search & Results */}
              {routeAuthGuardsFlag.isOn ? (
                <AffiliateAccessRoute path="/search/catalog/results" component={CatalogResultsPage} />
              ) : (
                <RecordAuthRoute path="/search/catalog/results" component={CatalogResultsPage} />
              )}
              <AnonSessionRoute
                path="/search/catalog"
                exact
                component={CatalogSearchPage}
                requirePersonalData={!signedIn}
              />
              {/* Catalog Print List */}
              {routeAuthGuardsFlag.isOn ? (
                <AffiliateAccessRoute path="/search/catalog/print-list" component={CatalogPrintListPage} />
              ) : (
                <RecordAuthRoute path="/search/catalog/print-list" component={CatalogPrintListPage} />
              )}
              {/* Catalog Film DGS-to-ARK redirect */}
              {routeAuthGuardsFlag.isOn ? (
                <AffiliateAccessRoute path="/search/catalog/film" component={CatalogFilmRedirectPage} />
              ) : (
                <RecordAuthRoute path="/search/catalog/film" component={CatalogFilmRedirectPage} />
              )}
              {/* Catalog Details */}
              {catalogShowRedirectUrl && <Redirect exact from="/search/catalog/show" to={catalogShowRedirectUrl} />}
              {/* Deprecated catalog paths */}
              <Redirect exact from="/search/catalog-search" to={catalogDeprecatedPathUrl} />
              <Redirect from="/catalog-search" to={catalogDeprecatedPathUrl} />
              <Redirect from="/catalog/search" to={catalogDeprecatedPathUrl} />
              <Redirect from="/eng/library/fhlc" to={catalogDeprecatedPathUrl} />
              <Redirect from="/eng/library/fhlcatalog" to={catalogDeprecatedPathUrl} />
              <Redirect from="/eng/library/fhlc/frameset_fhlc.asp" to={catalogDeprecatedPathUrl} />
              <Redirect
                from="/search/collection/results"
                to={{
                  pathname: pathname.replace('/search/collection/results', '/search/record/results'),
                  search,
                }}
              />
              {/* Handle old version of search-bifrost routes (this only handles /frontier/search-bifrost/<path-and-query> -> /search/<path-and-query> */}
              <Redirect
                from="/frontier/search-bifrost"
                to={{
                  pathname: pathname.replace('/frontier/search-bifrost', '/search'),
                  search,
                }}
              />
              <Redirect
                from="/search/search"
                to={{
                  pathname: pathname.replace('/search/search', '/search'),
                  search,
                }}
              />
              <Redirect
                from="/search/collection/results"
                to={{
                  pathname: pathname.replace('/search/collection/results', '/search/record/results'),
                  search,
                }}
              />
              {routeAuthGuardsFlag.isOn ? (
                <AffiliateAccessRoute path="/search/catalog/jiapu/:catId" component={JiapuPage} />
              ) : (
                <RecordAuthRoute path="/search/catalog/jiapu/:catId" component={JiapuPage} />
              )}
              {routeAuthGuardsFlag.isOn ? (
                <AffiliateAccessRoute path="/search/catalog(/oclc)?/:catId" component={FlaggedCatalogDetails} />
              ) : (
                <RecordAuthRoute path="/search/catalog(/oclc)?/:catId" component={FlaggedCatalogDetails} />
              )}
              {/* hidden routes; users shouldn't visit these */}
              <AuthRoute path="/search/pdf-test" component={PdfTestPage} LoadingSkeleton={<CenteredSpinner />} />
              <AnonSessionRoute path="/search/filmdebug" component={FilmDebugPage} />
              <AuthRoute path="/search/hrcompare" component={HRComparePage} />
              <AnonSessionRoute path="/search/recorddebug" component={RecordDebugPage} />
              <Route path="/search/blank" component={() => 'This page is intentionally left blank'} />
              <AuthRoute path="/search/jiapu-attach" component={JiapuAttach} />
              <AnonSessionRoute path="/search/mormon-genealogy" component={MormonGenealogicalResearchPage} />
              {/* no route found */}
              <Route component={NotFound} />
            </Switch>
          }
        </Suspense>
      </ErrorBoundary>
    </div>
  )
}

function getRedirectUrlForLangSlashArk({ pathname, search }) {
  const langCodePathWithArkRegex = /^\/(?<localeCode>[a-z]{2,3}(-[a-zA-Z0-9-]*)?\/)ark:\/61903\// // Copied from DTM haproxy config MATCH_root_lang_path_with_slash acl
  // using window location instead of useLocation since /<langCodePath> is missing from useLocation pathname
  const matches = window?.location?.pathname?.match(langCodePathWithArkRegex)
  const beginLocale = matches?.groups?.localeCode || ''

  if (beginLocale) {
    // e.g. /en/ark:/61903/3:1:33S7-9YB9-9JZF?cc=1727033 -> /ark:/61903/3:1:3Q9M-CS8L-9J?i=1&cc=1968530
    return `${pathname.replace(beginLocale, '')}${search}`
  }

  return null
}

export default App
